// RSSPage - RSS feed subscription and management page
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useMessage } from '../../context/MessageContext';
import { Rss, Plus, Trash2, RefreshCw, ExternalLink, Clock } from 'lucide-react';
import {
    getRssFeeds as getUserFeeds,
    addRssFeed as addFeed,
    deleteRssFeed as deleteFeed,
    refreshRssFeed as refreshFeed,
    getFeedItems,
    validateRssUrl
} from '../../services/rssService';
import './RSSPage.css';

const RSSPage = ({ onGenerateFromArticle, onPermissionDenied }) => {
    const { token } = useAuth();
    const { showMessage } = useMessage();

    const [feeds, setFeeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [forbidden, setForbidden] = useState(false);
    const [selectedFeed, setSelectedFeed] = useState(null);
    const [feedItems, setFeedItems] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);

    // Fetch user feeds
    useEffect(() => {
        const fetchFeeds = async () => {
            if (!token) return;
            setLoading(true);
            try {
                const result = await getUserFeeds(token);
                setFeeds(result || []);
                setForbidden(false);
            } catch (error) {
                if (error?.status === 403) {
                    setForbidden(true);
                    showMessage('warning', '当前账号无 RSS 管理权限（仅超管可访问）');
                    onPermissionDenied?.();
                    return;
                }
                showMessage('error', error.message || '获取订阅列表失败');
                console.error('Fetch feeds failed:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchFeeds();
    }, [token, showMessage, onPermissionDenied]);

    // Fetch feed items when a feed is selected
    useEffect(() => {
        const fetchItems = async () => {
            if (!selectedFeed) {
                setFeedItems([]);
                return;
            }
            try {
                const result = await getFeedItems(selectedFeed.id, token);
                setFeedItems(result || []);
            } catch (error) {
                console.error('Fetch items failed:', error);
            }
        };
        fetchItems();
    }, [selectedFeed, token]);

    const handleAddFeed = async (url, name) => {
        try {
            // Validate URL first
            const validation = await validateRssUrl(url);
            if (!validation.valid) {
                showMessage('error', 'RSS 链接无效或无法访问');
                return;
            }

            const result = await addFeed({ url, name: name || validation.title }, token);
            setFeeds([...feeds, result]);
            setShowAddModal(false);
            showMessage('success', '订阅添加成功！');
        } catch (error) {
            if (error?.status === 403) {
                setForbidden(true);
                showMessage('warning', '当前账号无 RSS 管理权限（仅超管可访问）');
                onPermissionDenied?.();
                return;
            }
            showMessage('error', error.message || '添加失败');
        }
    };

    const handleDeleteFeed = async (id) => {
        if (!window.confirm('确定取消订阅该源？')) return;
        try {
            await deleteFeed(id, token);
            setFeeds(feeds.filter(f => f.id !== id));
            if (selectedFeed?.id === id) {
                setSelectedFeed(null);
            }
            showMessage('success', '已取消订阅');
        } catch (error) {
            if (error?.status === 403) {
                setForbidden(true);
                showMessage('warning', '当前账号无 RSS 管理权限（仅超管可访问）');
                onPermissionDenied?.();
                return;
            }
            showMessage('error', error.message || '删除失败');
        }
    };

    const handleRefreshFeed = async (id) => {
        try {
            await refreshFeed(id, token);
            // Refresh items if this feed is selected
            if (selectedFeed?.id === id) {
                const result = await getFeedItems(id, token);
                setFeedItems(result || []);
            }
            showMessage('success', '刷新成功！');
        } catch (error) {
            if (error?.status === 403) {
                setForbidden(true);
                showMessage('warning', '当前账号无 RSS 管理权限（仅超管可访问）');
                onPermissionDenied?.();
                return;
            }
            showMessage('error', error.message || '刷新失败');
        }
    };

    if (forbidden) {
        return (
            <div className="rss-page min-h-screen bg-gray-50 flex items-center justify-center p-8">
                <div className="max-w-md w-full bg-white border border-gray-100 rounded-2xl shadow-sm p-6 text-center">
                    <Rss className="w-10 h-10 text-orange-400 mx-auto mb-3" />
                    <h2 className="text-lg font-bold text-gray-900 mb-2">暂无 RSS 管理权限</h2>
                    <p className="text-sm text-gray-500 mb-4">该入口仅对超管开放。你可以返回热点页继续创作闭环。</p>
                    <button
                        onClick={() => onPermissionDenied?.()}
                        className="px-4 py-2 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 text-sm font-medium"
                    >
                        返回热点页
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="rss-page min-h-screen bg-gray-50">
            <div className="flex h-screen">
                {/* Sidebar - Feed List */}
                <div className="w-72 bg-white border-r border-gray-100 flex flex-col">
                    {/* Header */}
                    <div className="p-4 border-b border-gray-100">
                        <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <Rss className="w-5 h-5 text-orange-500" />
                            RSS 订阅
                        </h1>
                    </div>

                    {/* Add Button */}
                    <div className="p-3">
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg
                bg-orange-50 text-orange-600 font-medium hover:bg-orange-100 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            添加订阅源
                        </button>
                    </div>

                    {/* Feed List */}
                    <div className="flex-1 overflow-y-auto p-3 space-y-1">
                        {loading ? (
                            <div className="flex justify-center py-8">
                                <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
                            </div>
                        ) : feeds.length === 0 ? (
                            <div className="text-center py-8 text-gray-400 text-sm">
                                暂无订阅
                            </div>
                        ) : (
                            feeds.map(feed => (
                                <button
                                    key={feed.id}
                                    onClick={() => setSelectedFeed(feed)}
                                    className={`
                    w-full text-left p-3 rounded-lg transition-colors
                    ${selectedFeed?.id === feed.id
                                            ? 'bg-orange-50 text-orange-700'
                                            : 'hover:bg-gray-50 text-gray-700'}
                  `}
                                >
                                    <div className="font-medium truncate">{feed.name}</div>
                                    <div className="text-xs text-gray-400 truncate mt-0.5">{feed.url}</div>
                                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                                        <Clock className="w-3 h-3" />
                                        {feed.last_updated ? new Date(feed.last_updated).toLocaleDateString() : '从未更新'}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Main Content - Feed Items */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {selectedFeed ? (
                        <>
                            {/* Feed Header */}
                            <div className="p-4 bg-white border-b border-gray-100 flex items-center justify-between">
                                <div>
                                    <h2 className="font-bold text-gray-900">{selectedFeed.name}</h2>
                                    <p className="text-xs text-gray-400">{selectedFeed.url}</p>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleRefreshFeed(selectedFeed.id)}
                                        className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                                        title="刷新"
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteFeed(selectedFeed.id)}
                                        className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                                        title="取消订阅"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Articles List */}
                            <div className="flex-1 overflow-y-auto p-4">
                                {feedItems.length === 0 ? (
                                    <div className="text-center py-16 text-gray-400">
                                        暂无文章
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {feedItems.map(item => (
                                            <div
                                                key={item.id}
                                                className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md transition-shadow"
                                            >
                                                <div className="flex items-start gap-4">
                                                    {/* Thumbnail */}
                                                    {item.thumbnail && (
                                                        <img
                                                            src={item.thumbnail}
                                                            alt=""
                                                            className="w-24 h-16 rounded-lg object-cover flex-shrink-0"
                                                        />
                                                    )}

                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="font-medium text-gray-900 line-clamp-1">
                                                            {item.title}
                                                        </h3>
                                                        <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                                                            {item.description || item.summary}
                                                        </p>
                                                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                                                            <span>{new Date(item.published_at).toLocaleDateString()}</span>
                                                            {item.author && <span>· {item.author}</span>}
                                                        </div>
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="flex gap-2">
                                                        <a
                                                            href={item.link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                                                            title="查看原文"
                                                        >
                                                            <ExternalLink className="w-4 h-4" />
                                                        </a>
                                                        <button
                                                            onClick={() => onGenerateFromArticle?.(item)}
                                                            className="px-3 py-1.5 rounded-lg bg-orange-100 text-orange-600 text-sm font-medium hover:bg-orange-200"
                                                        >
                                                            生成内容
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-gray-400">
                            <div className="text-center">
                                <Rss className="w-16 h-16 mx-auto mb-4 opacity-50" />
                                <p>选择一个订阅源查看文章</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add Feed Modal */}
            {showAddModal && (
                <AddFeedModal
                    onAdd={handleAddFeed}
                    onClose={() => setShowAddModal(false)}
                />
            )}
        </div>
    );
};

// Add Feed Modal
const AddFeedModal = ({ onAdd, onClose }) => {
    const [url, setUrl] = useState('');
    const [name, setName] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        onAdd(url, name);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl">
                <div className="p-5 border-b border-gray-100">
                    <h2 className="font-bold text-lg text-gray-900">添加 RSS 订阅</h2>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">RSS 链接 *</label>
                        <input
                            type="url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            required
                            placeholder="https://example.com/feed.xml"
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">自定义名称</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="留空使用源标题"
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                        />
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100"
                        >
                            取消
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 rounded-lg bg-orange-600 text-white font-medium hover:bg-orange-700"
                        >
                            添加订阅
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RSSPage;
